import { ValidatorSchema } from './schema.model';
import { ValidationErrors } from '@angular/forms';

export function validate<T>(
  obj: Partial<T> | Partial<T>[] | undefined,
  schema: ValidatorSchema<T>
): ValidationErrors | ValidationErrors[] | null {
  // Handle the case where obj is an array.
  if (Array.isArray(obj)) {
    if (obj.length === 0) {
      // Simulate a single iteration with an empty object.
      const simulatedError = validate({} as Partial<T>, schema);
      // Even if simulated validation returns no errors, enforce a minimum one element error.
      return [simulatedError || { minItems: 'At least one element is required' }];
    } else {
      const errorsArray = obj.map((item) => validate(item, schema));
      return errorsArray.every((err) => err === null) ? null : errorsArray;
    }
  }

  const errors: { [key: string]: any } = {};
  const target = obj || ({} as Partial<T>);

  // Step 1: Run property-level validation (skip special keys)
  for (const key in schema) {
    if (!Object.prototype.hasOwnProperty.call(schema, key) || key === '$cross' || key === '$omit') continue;
    const schemaItem = schema[key as keyof T];
    const value = target[key as keyof T];

    if (typeof schemaItem === 'function' || Array.isArray(schemaItem)) {
      // Property-level validators (not nested schemas)
      const validators = typeof schemaItem === 'function' ? [schemaItem] : schemaItem;
      let fieldErrors: ValidationErrors = {};
      for (const validator of validators) {
        const result = validator(value as any);
        if (result) {
          fieldErrors = { ...fieldErrors, ...result };
        }
      }
      if (Object.keys(fieldErrors).length > 0) {
        errors[key] = fieldErrors;
      }
    }
    // For nested objects or arrays, handle recursively.
    else if (typeof schemaItem === 'object' && schemaItem !== null) {
      if (Array.isArray(value)) {
        const arrayErrors: any[] = [];
        if (value.length === 0) {
          // Simulate one iteration with an empty object.
          const itemErrors = validate({} as any, schemaItem as any);
          arrayErrors.push(itemErrors || { minItems: 'At least one element is required' });
        } else {
          value.forEach((item, index) => {
            const itemErrors = validate(item, schemaItem as any);
            if (itemErrors) {
              arrayErrors[index] = itemErrors;
            }
          });
        }
        if (arrayErrors.length > 0) {
          errors[key] = arrayErrors;
        }
      } else {
        // Nested object: call validate recursively.
        const nestedErrors = validate(value as Partial<T[keyof T]>, schemaItem as any);
        if (nestedErrors) {
          errors[key] = nestedErrors;
        }
      }
    }
  }

  // Step 2: Run cross validators (if defined)
  if (schema.$cross) {
    const crossValidators = typeof schema.$cross === 'function' ? [schema.$cross] : schema.$cross;
    let crossErrors: ValidationErrors = {};
    for (const validator of crossValidators) {
      const result = validator(obj as T);
      if (result) {
        crossErrors = { ...crossErrors, ...result };
      }
    }
    if (Object.keys(crossErrors).length > 0) {
      errors['$cross'] = crossErrors;
    }
  }

  // Step 3: Run $omit rules (if defined)
  if (schema.$omit) {
    const omitRules = Array.isArray(schema.$omit) ? schema.$omit : [schema.$omit];
    for (const rule of omitRules) {
      if (rule.condition(obj as T)) {
        // Remove any errors for the specified field.
        delete errors[rule.field as string];
      }
    }
  }

  return Object.keys(errors).length > 0 ? errors : null;
}

export function flattenErrors(obj: any, prefix = ''): Set<string> {
  const result = new Set<string>();

  for (const key in obj) {
    const value = obj[key];
    // Build the new prefix path (if prefix exists, append with a dot)
    const newPrefix = prefix ? `${prefix}.${key}` : key;

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      // Determine if this object is a "leaf" (all values are non-objects or null)
      const isLeaf = Object.values(value).every((val) => typeof val !== 'object' || val === null);
      if (isLeaf) {
        // For leaf objects, add each key as part of the error path.
        for (const errorKey in value) {
          result.add(`${newPrefix}.${errorKey}`);
        }
      } else {
        // Otherwise, recursively flatten deeper and merge the result.
        const nested = flattenErrors(value, newPrefix);
        nested.forEach((item) => result.add(item));
      }
    } else {
      // In case of a primitive value, add the path directly.
      result.add(newPrefix);
    }
  }

  return result;
}
